import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient, HttpParams } from '@angular/common/http';
import { CoreDataService } from '@app/core/async-services/http/core.data';
import { Platform } from '@ionic/angular';
import { environment } from '@env/environment';
import { CustomEncoder } from '../http.util';
import { API } from '@app/core/models';
import { getOptions } from '../http.helper';
import {
  IsSessionValidResponse,
  loginResponse,
  PubNubConfigResponse,
} from './auth.interface';
import { Observable } from 'rxjs';
import { PubnubConfig } from 'pubnub';
import { map } from 'rxjs/operators';
import { ApiVersionedRoute } from './api-versions';
import {
  CommunicationKeys,
  CommunicationKeysResponse,
} from '@app/core/models/pubnub.interface';
import { HttpOptions } from '@app/core/models/api.interface';

@Injectable({
  providedIn: 'root',
})
export class AuthHttpService {
  constructor(
    private _platform: Platform,
    private _coreDataService: CoreDataService,
    private _http: HttpClient,
  ) {}

  login(body: object) {
    let options = {};
    if (!this._platform.is('capacitor')) {
      const headers = new HttpHeaders({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Authorization: '',
      });
      options = {
        headers,
        withCredentials: true,
      };
    }

    return this._coreDataService.post<API.Response<loginResponse>>(
      ApiVersionedRoute.auth,
      body,
      options,
    );
  }

  getSession(): Observable<boolean> {
    const headers = new HttpHeaders();
    const options = {
      headers,
      withCredentials: true,
    };

    return this._coreDataService
      .get<API.Response<IsSessionValidResponse>>(
        `${ApiVersionedRoute.auth}/session`,
        options,
      )
      .pipe(map((response) => response.payload?.isSessionValid as boolean));
  }

  logout() {
    const headers = new HttpHeaders();
    const options = {
      headers,
      withCredentials: true,
    };
    return this._coreDataService.get<any>(
      `${ApiVersionedRoute.auth}/logout`,
      options,
    );
  }

  getCognitoKeys() {
    return this._http.get(environment.aws.cognito.keyUrl);
  }

  forgotPassword(username: string) {
    const headers = new HttpHeaders();
    const options = {
      headers,
      withCredentials: true,
    };

    return this._coreDataService.get<any>(
      `${ApiVersionedRoute.auth}/password?id=${username}`,
      options,
    );
  }

  confirmForgotPassword(body: object) {
    const headers = new HttpHeaders();
    const options = {
      headers,
      withCredentials: true,
    };

    return this._coreDataService.post<any>(
      `${ApiVersionedRoute.auth}/password/`,
      body,
      options,
    );
  }

  checkConfirmationCode(username: string, confirmationCode: number) {
    const headers = new HttpHeaders();
    const options: HttpOptions = {
      headers,
      withCredentials: true,
      params: null,
    };
    options.params = new HttpParams({
      encoder: new CustomEncoder(),
    })
      .set('username', username)
      .set('code', confirmationCode.toString());

    return this._coreDataService.get<any>(
      `${ApiVersionedRoute.auth}/password/validate`,
      options,
    );
  }

  changePassword(body: object) {
    const headers = new HttpHeaders();
    const options = {
      headers,
      withCredentials: true,
    };

    return this._coreDataService.post<any>(
      `${ApiVersionedRoute.auth}/password/update`,
      body,
      options,
    );
  }

  getPublicEncryptionKey() {
    const options = {};

    return this._coreDataService.get<API.GetPublicEncryptionKeySuccess>(
      `${ApiVersionedRoute.auth}/key`,
      options,
    );
  }

  getTimeAndAttendanceLinks() {
    const headers = new HttpHeaders();
    const options = {
      headers,
      withCredentials: true,
    };

    return this._coreDataService.get<any>(
      `${ApiVersionedRoute.auth}/tnalinks`,
      options,
    );
  }

  getTimeAndAttendanceIframeUrl(id: string) {
    const headers = new HttpHeaders();
    const options: HttpOptions = {
      headers,
      withCredentials: true,
      params: null,
    };
    options.params = new HttpParams({
      encoder: new CustomEncoder(),
    }).set('id', id);

    return this._coreDataService.get<any>(
      `${ApiVersionedRoute.auth}/tnaiframeurl`,
      options,
    );
  }

  getPubNubConfig(): Observable<PubnubConfig> {
    const options = getOptions(this._platform);
    return this._coreDataService
      .get<PubNubConfigResponse>(
        `${ApiVersionedRoute.auth}/pubnub-config`,
        options,
      )
      .pipe(map((response) => response.payload?.pubnubConfig as PubnubConfig));
  }

  getCommunicationKeys(): Observable<CommunicationKeys> {
    const options = this._coreDataService.getHttpOptions();
    return this._coreDataService
      .get<CommunicationKeysResponse>(
        `${ApiVersionedRoute.auth}/communication-keys`,
        options,
      )
      .pipe(map((response) => response.payload.keys));
  }
}
